import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import axios from "axios";
import "./utils/authToken";
import {
  getBootstrappedImpersonationToken,
  getAuthToken,
  isFranchisePortalAuthSession,
  isRestaurantImpersonationAuthSession,
} from "./utils/authToken";
import App from "./App.jsx";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store/store";
import { loginConfirmedAction, LOGOUT_ACTION } from "./store/actions/AuthActions";
import ThemeContext from "./context/ThemeContext.jsx";
import {
  clearRestaurantSession,
  forceLogoutToLogin,
  isTokenExpired,
} from "./utils/sessionAuth";
import {
  getAdminDeviceId,
  startAdminInactivityWatch,
} from "./utils/adminInactivity";

let isLoggingOut = false;

function handleUnauthorizedSession() {
  if (isLoggingOut) return;
  isLoggingOut = true;
  clearRestaurantSession();
  store.dispatch({ type: LOGOUT_ACTION });
  forceLogoutToLogin();
}

axios.interceptors.request.use((requestConfig) => {
  const token = getAuthToken();
  const url = String(requestConfig.url || "");
  const isSignIn = /\/auth\/(login|verify-2fa)/i.test(url);
  if (
    isSignIn ||
    (token && !isFranchisePortalAuthSession() && !isRestaurantImpersonationAuthSession())
  ) {
    requestConfig.headers = requestConfig.headers || {};
    requestConfig.headers["x-admin-device"] = getAdminDeviceId();
  }
  return requestConfig;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const hasAuthHeader = Boolean(error?.config?.headers?.Authorization);
    const isAuthEndpoint =
      url.includes("/auth/login") ||
      url.includes("/auth/verify-2fa") ||
      url.includes("/auth/forgot") ||
      url.includes("/auth/reset") ||
      url.includes("/forgot") ||
      url.includes("/reset-password");

    if (status === 401 && hasAuthHeader && !isAuthEndpoint) {
      handleUnauthorizedSession();
    }
    return Promise.reject(error);
  }
);

const existingToken = getAuthToken();
if (existingToken && isTokenExpired(existingToken)) {
  handleUnauthorizedSession();
}

const tabScopedToken =
  isRestaurantImpersonationAuthSession() || isFranchisePortalAuthSession()
    ? getAuthToken()
    : null;
const impersonationToken =
  getBootstrappedImpersonationToken() || tabScopedToken;

if (impersonationToken && !isTokenExpired(impersonationToken)) {
  store.dispatch(
    loginConfirmedAction({
      idToken: impersonationToken,
      tokenLogin: impersonationToken,
    })
  );
}

startAdminInactivityWatch();

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <Provider store={store}>
      <BrowserRouter basename="/">
        <ThemeContext>
          <App />
        </ThemeContext>
      </BrowserRouter>
    </Provider>
  </StrictMode>
);
